import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { CONFIG } from '../config/apiConfig';

const PdfToExl = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [elapsed, setElapsed] = useState(0);   // seconds elapsed during conversion
    const inputRef = useRef(null);
    const timerRef = useRef(null);

    // Start / stop elapsed timer when loading changes
    useEffect(() => {
        if (loading) {
            setElapsed(0);
            timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
        } else {
            clearInterval(timerRef.current);
        }
        return () => clearInterval(timerRef.current);
    }, [loading]);

    const formatElapsed = (s) => {
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
    };

    const ACCEPTED_TYPES = [
        'application/pdf',
        'image/png',
        'image/jpeg',
        'image/webp',
        'image/tiff',
    ];
    const ACCEPTED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.webp', '.tif', '.tiff'];

    const handleFileSelect = (file) => {
        if (!file) return;
        const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
        if (!ACCEPTED_TYPES.includes(file.type) && !ACCEPTED_EXTENSIONS.includes(ext)) {
            setError('Please select a PDF or image file (PNG, JPG, WEBP, TIFF).');
            setSelectedFile(null);
            return;
        }
        setError(null);
        setSuccess(false);
        setSelectedFile(file);
    };

    const handleInputChange = (e) => handleFileSelect(e.target.files[0]);

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFileSelect(e.dataTransfer.files[0]);
    };

    const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
    const handleDragLeave = () => setDragOver(false);

    // Parse error message from a blob response (axios returns blob even for errors)
    const parseErrorBlob = async (blob) => {
        try {
            const text = await blob.text();
            const json = JSON.parse(text);
            return json.message || json.error || text;
        } catch {
            return 'Conversion failed. Please try again.';
        }
    };

    const handleSubmit = async () => {
        if (!selectedFile) {
            setError('Please select a PDF or image file first.');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            const formData = new FormData();
            formData.append('data_filename', selectedFile);

            const response = await axios.post(
                `${CONFIG.BASE_URL}/tp/pdf-to-exl`,
                formData,
                {
                    responseType: 'blob',
                    headers: { 'Content-Type': 'multipart/form-data' },
                    // Large PDFs (30+ pages) can take 2-3 minutes to process.
                    // Set a 5-minute timeout to avoid browser / proxy killing the request.
                    timeout: 300_000,
                }
            );

            // Check if the response is actually an error (e.g. 500 with JSON body)
            const contentType = response.headers['content-type'] || '';
            if (contentType.includes('application/json') || contentType.includes('text/')) {
                const msg = await parseErrorBlob(response.data);
                setError(msg);
                return;
            }

            // Determine filename from Content-Disposition header or fallback
            const disposition = response.headers['content-disposition'];
            let filename = selectedFile.name.replace(/\.(pdf|png|jpe?g|webp|tiff?)$/i, '.xlsx');
            if (disposition) {
                const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (match && match[1]) {
                    filename = match[1].replace(/['"]/g, '');
                }
            }

            // Trigger download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setSuccess(true);
        } catch (err) {
            // axios throws on non-2xx; err.response?.data is a Blob when responseType='blob'
            let msg = 'Conversion failed. Please try again.';
            if (err.code === 'ECONNABORTED' || (err.message || '').toLowerCase().includes('timeout')) {
                msg = 'The request timed out. Large files may take several minutes — please try again.';
            } else if (err.response?.data instanceof Blob) {
                msg = await parseErrorBlob(err.response.data);
            } else if (err.response?.data?.message) {
                msg = err.response.data.message;
            } else if (err.message) {
                msg = err.message;
            }
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setSelectedFile(null);
        setError(null);
        setSuccess(false);
        if (inputRef.current) inputRef.current.value = '';
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
            <div className="w-full max-w-lg">
                {/* Card */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-7">
                        <div className="flex items-center gap-3 mb-1">
                            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <h1 className="text-2xl font-bold text-white">PDF to Excel</h1>
                        </div>
                        <p className="text-blue-100 text-sm">Upload a PDF and we'll convert it to an Excel spreadsheet</p>
                    </div>

                    {/* Body */}
                    <div className="px-8 py-8 space-y-6">
                        {/* Drop zone */}
                        <div
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onClick={() => !selectedFile && inputRef.current?.click()}
                            className={`
                border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer
                ${dragOver
                                    ? 'border-blue-500 bg-blue-50'
                                    : selectedFile
                                        ? 'border-green-400 bg-green-50 cursor-default'
                                        : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'}
              `}
                        >
                            {selectedFile ? (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-center">
                                        <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <p className="text-sm font-semibold text-gray-800">{selectedFile.name}</p>
                                    <p className="text-xs text-gray-500">
                                        {(selectedFile.size / 1024).toFixed(1)} KB
                                    </p>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleReset(); }}
                                        className="text-xs text-red-500 hover:text-red-700 underline mt-1"
                                    >
                                        Remove file
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <div className="flex justify-center">
                                        <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-700">
                                            Drag &amp; drop your PDF or image here, or{' '}
                                            <span className="text-blue-600 hover:underline">browse</span>
                                        </p>
                                        <p className="text-xs text-gray-400 mt-1">PDF, PNG, JPG, WEBP, TIFF supported</p>
                                    </div>
                                </div>
                            )}
                            <input
                                ref={inputRef}
                                id="pdf-upload"
                                type="file"
                                accept=".pdf,.png,.jpg,.jpeg,.webp,.tif,.tiff,application/pdf,image/png,image/jpeg,image/webp,image/tiff"
                                onChange={handleInputChange}
                                className="hidden"
                            />
                        </div>

                        {/* Error */}
                        {error && (
                            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                                <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                        clipRule="evenodd" />
                                </svg>
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                        )}

                        {/* Success */}
                        {success && (
                            <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                                <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                        clipRule="evenodd" />
                                </svg>
                                <p className="text-sm text-green-700">
                                    Excel file downloaded successfully!
                                </p>
                            </div>
                        )}

                        {/* Submit button */}
                        <button
                            onClick={handleSubmit}
                            disabled={!selectedFile || loading}
                            className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl
                bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed
                text-white font-semibold text-sm transition-colors shadow-sm"
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin w-4 h-4 text-white" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                    <span>Converting… {formatElapsed(elapsed)}</span>
                                </>
                            ) : (
                                <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                    <span>Convert &amp; Download</span>
                                </>
                            )}
                        </button>

                        {/* Hint shown while loading */}
                        {loading && (
                            <p className="text-xs text-center text-gray-400">
                                Large files may take 1–3 minutes. Please keep this tab open.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PdfToExl;
